import {
  type DesktopCloudEnvironmentRecord,
  EnvironmentId,
  sanitizeSavedEnvironmentWsBaseUrl,
} from "@cmmd-forge/contracts";
import { setSavedEnvironmentRegistry } from "~/clientPersistenceApi";
import { bootstrapRemoteBearerSession, fetchRemoteEnvironmentDescriptor } from "../remote/api";
import { resolveRemotePairingTarget } from "../remote/target";
import { throwIfAborted } from "./abortableWait";
import {
  getSavedEnvironmentRecord,
  listSavedEnvironmentRecords,
  persistSavedEnvironmentRecord,
  removeSavedEnvironmentBearerToken,
  type SavedEnvironmentRecord,
  useSavedEnvironmentRegistryStore,
  writeSavedEnvironmentBearerToken,
} from "./catalog";
import { type EnvironmentConnection } from "./connection";
import { setRuntimeConnecting, setRuntimeError } from "./runtimeConnectionStatus";
import { useSavedEnvironmentRuntimeStore } from "./savedEnvironmentRuntimeStore";
import { type SavedEnvironmentConnectionOptions } from "./savedEnvironmentConnection";
import { type SavedEnvironmentAcquisitionRegistryExpectation } from "./savedEnvironmentConnectionRegistry";
import * as savedCloud from "./savedCloudEnvironmentLifecycle";

type ConnectedSavedEnvironmentBootstrapRetry = savedCloud.ConnectedSavedEnvironmentBootstrapRetry;

export type SavedEnvironmentOperationDependencies = {
  readonly readConnection: (environmentId: EnvironmentId) => EnvironmentConnection | null;
  readonly removeConnection: (environmentId: EnvironmentId) => Promise<boolean>;
  readonly ensureSavedEnvironmentConnection: (
    record: SavedEnvironmentRecord,
    options?: SavedEnvironmentConnectionOptions,
  ) => Promise<EnvironmentConnection>;
  readonly ensureCmmdRuntimeSavedEnvironmentConnection: (
    record: SavedEnvironmentRecord,
    options?: {
      readonly bearerToken?: string;
      readonly signal?: AbortSignal;
      readonly onBootstrapRetry?: (progress: ConnectedSavedEnvironmentBootstrapRetry) => void;
      readonly registryExpectation?: SavedEnvironmentAcquisitionRegistryExpectation;
    },
  ) => Promise<EnvironmentConnection>;
  readonly invalidate: (environmentId: EnvironmentId) => void;
  readonly retireEnvironmentId: (environmentId: EnvironmentId) => void;
};

function isoNow(): string {
  return new Date().toISOString();
}

export async function disconnectSavedEnvironment(
  environmentId: EnvironmentId,
  dependencies: SavedEnvironmentOperationDependencies,
): Promise<void> {
  const connection = dependencies.readConnection(environmentId);
  if (connection?.kind !== "saved") {
    return;
  }

  useSavedEnvironmentRuntimeStore.getState().clear(environmentId);
  await dependencies.removeConnection(environmentId).catch(() => false);
}

export async function reconnectSavedEnvironmentRecord(
  environmentId: EnvironmentId,
  dependencies: SavedEnvironmentOperationDependencies,
): Promise<void> {
  const record = getSavedEnvironmentRecord(environmentId);
  if (!record) {
    throw new Error("Saved environment not found.");
  }

  const connection = dependencies.readConnection(environmentId);
  if (!connection) {
    try {
      await dependencies.ensureCmmdRuntimeSavedEnvironmentConnection(record);
    } catch (error) {
      setRuntimeError(environmentId, error);
      throw error;
    }
    return;
  }

  setRuntimeConnecting(environmentId);
  try {
    await connection.reconnect();
  } catch (error) {
    setRuntimeError(environmentId, error);
    throw error;
  }
}

export async function removeSavedEnvironmentRecord(
  environmentId: EnvironmentId,
  dependencies: SavedEnvironmentOperationDependencies,
): Promise<void> {
  dependencies.invalidate(environmentId);
  useSavedEnvironmentRegistryStore.getState().remove(environmentId);
  await removeSavedEnvironmentBearerToken(environmentId);
  await disconnectSavedEnvironment(environmentId, dependencies);
}

export async function addSavedEnvironmentRecord(
  input: {
    readonly label: string;
    readonly pairingUrl?: string;
    readonly host?: string;
    readonly pairingCode?: string;
  },
  dependencies: SavedEnvironmentOperationDependencies,
): Promise<SavedEnvironmentRecord> {
  const resolvedTarget = resolveRemotePairingTarget({
    ...(input.pairingUrl !== undefined ? { pairingUrl: input.pairingUrl } : {}),
    ...(input.host !== undefined ? { host: input.host } : {}),
    ...(input.pairingCode !== undefined ? { pairingCode: input.pairingCode } : {}),
  });
  const descriptor = await fetchRemoteEnvironmentDescriptor({
    httpBaseUrl: resolvedTarget.httpBaseUrl,
  });
  const environmentId = descriptor.environmentId;

  if (dependencies.readConnection(environmentId) !== null) {
    throw new Error("This environment is already connected.");
  }

  const bearerSession = await bootstrapRemoteBearerSession({
    httpBaseUrl: resolvedTarget.httpBaseUrl,
    credential: resolvedTarget.credential,
  });

  const record: SavedEnvironmentRecord = {
    environmentId,
    label: input.label.trim() || descriptor.label,
    wsBaseUrl: resolvedTarget.wsBaseUrl,
    httpBaseUrl: resolvedTarget.httpBaseUrl,
    createdAt: isoNow(),
    lastConnectedAt: isoNow(),
  };

  await persistSavedEnvironmentRecord(record);
  const didPersistBearerToken = await writeSavedEnvironmentBearerToken(
    environmentId,
    bearerSession.sessionToken,
  );
  if (!didPersistBearerToken) {
    await setSavedEnvironmentRegistry(listSavedEnvironmentRecords());
    throw new Error("Unable to persist saved environment credentials.");
  }
  await dependencies.ensureSavedEnvironmentConnection(record, {
    bearerToken: bearerSession.sessionToken,
    role: bearerSession.role,
    registryExpectation: { kind: "initial-add" },
  });
  useSavedEnvironmentRegistryStore
    .getState()
    .upsert(savedCloud.mergeSavedEnvironmentCapability(record));
  return record;
}

/** Registers a cloud Environment CMMD already connected, replacing a legacy-id record. */
export async function addConnectedSavedEnvironmentRecord(
  input: {
    readonly environment: DesktopCloudEnvironmentRecord;
    readonly bearerToken: string;
    readonly signal?: AbortSignal;
    readonly onBootstrapRetry?: (progress: ConnectedSavedEnvironmentBootstrapRetry) => void;
  },
  dependencies: SavedEnvironmentOperationDependencies,
): Promise<SavedEnvironmentRecord> {
  const environmentId = EnvironmentId.make(input.environment.environmentId);
  const { existing, replacedRecord } = savedCloud.resolveSavedEnvironmentReplacement({
    environmentId,
    replacesEnvironmentId: input.environment.replacesEnvironmentId,
  });
  const record = connectedSavedEnvironmentRecord(input.environment, existing);
  throwIfAborted(input.signal);
  const persistenceRollback = await savedCloud.captureCloudPersistenceRollback(existing);
  const keepsLiveConnection =
    dependencies.readConnection(record.environmentId) !== null &&
    savedCloud.registersConnectedTarget({ existing, replacedRecord, record });
  if (!keepsLiveConnection) {
    dependencies.invalidate(record.environmentId);
    await dependencies.removeConnection(record.environmentId).catch(() => false);
  }
  if (replacedRecord) {
    dependencies.invalidate(replacedRecord.environmentId);
    await dependencies.removeConnection(replacedRecord.environmentId).catch(() => false);
  }
  await dependencies.ensureCmmdRuntimeSavedEnvironmentConnection(record, {
    bearerToken: input.bearerToken,
    registryExpectation:
      existing && !replacedRecord
        ? { kind: "existing-record", record: existing }
        : { kind: "initial-add" },
    ...(input.signal ? { signal: input.signal } : {}),
    ...(input.onBootstrapRetry ? { onBootstrapRetry: input.onBootstrapRetry } : {}),
  });
  return persistConnectedSavedEnvironment({
    record,
    replacedRecord,
    persistenceRollback,
    keepsLiveConnection,
    signal: input.signal,
    dependencies,
  });
}

function connectedSavedEnvironmentRecord(
  environment: DesktopCloudEnvironmentRecord,
  existing: SavedEnvironmentRecord | null,
): SavedEnvironmentRecord {
  const environmentId = EnvironmentId.make(environment.environmentId);
  const { kind, cmmdOrganizationId, cmmdProjectId, runtimeId, runtimeTicketExpiresAt } =
    environment;
  return {
    environmentId,
    label: environment.label,
    wsBaseUrl: sanitizeSavedEnvironmentWsBaseUrl(environment.wsBaseUrl),
    httpBaseUrl: environment.httpBaseUrl,
    createdAt: existing?.createdAt ?? isoNow(),
    lastConnectedAt: isoNow(),
    ...(kind === undefined ? {} : { kind }),
    ...(cmmdOrganizationId === undefined ? {} : { cmmdOrganizationId }),
    ...(cmmdProjectId === undefined ? {} : { cmmdProjectId }),
    ...(runtimeId === undefined ? {} : { runtimeId }),
    ...(runtimeTicketExpiresAt === undefined ? {} : { runtimeTicketExpiresAt }),
  };
}

async function persistConnectedSavedEnvironment(input: {
  readonly record: SavedEnvironmentRecord;
  readonly replacedRecord: SavedEnvironmentRecord | null;
  readonly persistenceRollback: Awaited<
    ReturnType<typeof savedCloud.captureCloudPersistenceRollback>
  >;
  readonly keepsLiveConnection: boolean;
  readonly signal: AbortSignal | undefined;
  readonly dependencies: SavedEnvironmentOperationDependencies;
}): Promise<SavedEnvironmentRecord> {
  const { record, replacedRecord, persistenceRollback, keepsLiveConnection, signal, dependencies } =
    input;
  let didPersistRecord = false;
  try {
    throwIfAborted(signal);
    await savedCloud.persistConnectedCloudEnvironment({
      record,
      replacedRecord,
      rollback: persistenceRollback,
      ...(signal ? { signal } : {}),
    });
    didPersistRecord = true;
    throwIfAborted(signal);
    useSavedEnvironmentRegistryStore
      .getState()
      .upsert(savedCloud.mergeSavedEnvironmentCapability(record));
    // Only a committed move retires the replaced id: until then its record is the one on disk.
    if (replacedRecord) dependencies.retireEnvironmentId(replacedRecord.environmentId);
    return record;
  } catch (error) {
    if (!keepsLiveConnection) {
      await dependencies.removeConnection(record.environmentId).catch(() => false);
    }
    if (didPersistRecord) {
      await savedCloud.restoreCloudEnvironmentPersistence({
        record,
        replacedRecord,
        rollback: persistenceRollback,
      });
    }
    throwIfAborted(signal);
    throw error;
  }
}
